import { inject, Injectable } from '@angular/core';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum, EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { User } from '@app/core/model/client/user';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { AppManager } from '@services/managers/app.manager';
import { Angulartics2 } from 'angulartics2';

@Injectable()
export class AnalyticsService {

  private currentPageName: string;

  private angulartics2 = inject(Angulartics2);
  private translate = inject(TranslateService);
  private appManager = inject(AppManager);

  constructor() {
    // Deactivate the automatic tracking on url
    this.angulartics2.settings.pageTracking.autoTrackVirtualPages = false;
  }

  /**
   * Set general information about one session
   * @param user Current authenticated User
   */
  public setSessionInformation(user: User) {
    this.angulartics2.setUserProperties.next(
      {
        clients: user.clients.map((client) => {
          return client.id;
        }),
        role: user.userRole.id,
        isTb: user.userRole.isTb,
        id: user.id
      });
    this.angulartics2.setUsername.next(user.id);
  }

  /**
   * Track current page and send page title to mixpanel
   * @param currentPageName The page or tab name
   */
  public trackDisplayPageEvent(currentPageName: string) {
    this.currentPageName = currentPageName;
    this.trackEvent(new AnalyticsEvent(ActionEnum.READ)
    );
  }

  /**
   * Track navigation events from user actions.
   * @param eventOrigin Action's location
   * @param navigateTo Destination
   * @param entityType Action's entity type
   * @param entityId Action's entity ID
   */
  public trackNavigationEvent(eventOrigin: string, navigateTo: string, entityType?: string, entityId?: string) {
    const analyticsEvent = new AnalyticsEvent(ActionEnum.NAVIGATE)
      .addProperties({
        [AnalyticsKeyEnum.EVENT_ORIGIN]: eventOrigin,
        [AnalyticsKeyEnum.NAVIGATE_TO]: this.translate.instant(navigateTo)
      });
    if (entityType) {
      analyticsEvent.addProperties({ [AnalyticsKeyEnum.ENTITY_TYPE]: entityType });
    }
    if (entityId) {
      analyticsEvent.addProperties({ [AnalyticsKeyEnum.ENTITY_ID]: entityId });
    }
    this.trackEvent(analyticsEvent);
  }

  /**
   * Track actions in grids
   * @param gridActionType User action in the grid.
   * @param entityType Grid's entities type
   */
  public trackGridActionEvent(gridActionType: string, entityType: string) {
    const analyticsEvent = new AnalyticsEvent(ActionEnum.GRID)
      .addProperties({
        [AnalyticsKeyEnum.GRID_ACTION_TYPE]: gridActionType,
        [AnalyticsKeyEnum.ENTITY_TYPE]: entityType
      });
    this.trackEvent(analyticsEvent);
  }

  /**
   *  Track actions in inline fields
   * @param field Field or FieldGroup code
   * @param inlineAction Action performed
   * @param eventOrigin Action's location
   */
  public trackInlineActionEvent(field: string, inlineAction: ActionEnum, eventOrigin: EventOriginEnum) {
    const analyticsEvent = new AnalyticsEvent(ActionEnum.UPDATE)
      .addProperties({
        [AnalyticsKeyEnum.FIELD]: field,
        [AnalyticsKeyEnum.INLINE_ACTION]: inlineAction,
        [AnalyticsKeyEnum.EVENT_ORIGIN]: eventOrigin
      });
    if (this.appManager.currentEntity && this.appManager.currentEntity.objectType) {
      analyticsEvent.addProperties({
        [AnalyticsKeyEnum.ENTITY_TYPE]: this.appManager.currentEntity.objectType,
        [AnalyticsKeyEnum.ENTITY_ID]: this.appManager.currentEntity.id
      });
    }
    this.trackEvent(analyticsEvent);
  }

  /**
   * Add page information to analyticsEvent then send it to mixpanel
   * @param analyticsEvent Event to send to mixpanel
   */
  public trackEvent(analyticsEvent: AnalyticsEvent): void {
    if (environment.production === true) {
      this.angulartics2.eventTrack.next(
        analyticsEvent.addProperties({ [AnalyticsKeyEnum.CURRENT_PAGE]: this.currentPageName })
          .buildEventTrack()
      );
    }
  }
}
